"use client"

import { CTABlockContent, CTAButton, Theme, defaultTheme } from "@/types"
import { BlockImage } from "../BlockImage"
import { RichTextRenderer } from "../RichTextRenderer"

interface CTABlockProps {
  content: CTABlockContent
  onAnswer?: (answer: any) => void
  answer?: any
  isShopifyProxy?: boolean
  shopDomain?: string | null
  projectId?: string
  onCtaClick?: (stepId?: string, metadata?: any) => void
  onAutoAdvance?: () => void
  onAnswerEvent?: (answer: string) => void
  theme?: Theme | null
  previewMode?: "desktop" | "mobile"
}

export function CTABlock({ 
  content, 
  onAnswer, 
  isShopifyProxy = false, 
  shopDomain = null,
  projectId,
  onCtaClick,
  onAutoAdvance,
  onAnswerEvent,
  theme = null
}: CTABlockProps) {
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme

  // Migrate legacy single-button format to buttons array
  const buttons: CTAButton[] = content.buttons && content.buttons.length > 0 
    ? content.buttons 
    : [{
        id: 'legacy',
        buttonText: content.buttonText || "Get Started",
        mode: content.mode || 'externalUrl',
        url: content.url || content.buttonUrl || (content.handle && shopDomain ? `https://${shopDomain}/products/${content.handle}` : "")
      }]

  const handleButtonClick = (button: CTAButton) => {
    // Track which button was clicked as an answer (for analytics)
    onAnswerEvent?.(button.buttonText)
    
    if (button.mode === 'nextStep') {
      onAnswer?.("clicked")
      
      // Track the click event (for engagement billing)
      onCtaClick?.(undefined, {
        ctaType: 'nextStep',
        buttonText: button.buttonText,
      })
      
      // Trigger advancement to next step
      onAutoAdvance?.()
    } else {
      // externalUrl mode
      if (button.url) {
        window.open(button.url, '_blank')
      }
      
      onAnswer?.("clicked")
      
      // Track CTA click event (for engagement billing)
      onCtaClick?.(undefined, {
        ctaType: 'externalUrl',
        buttonText: button.buttonText,
        targetUrl: button.url,
      })
    }
  }

  return (
    <div className="text-center">
      {/* CTA Text */}
      <div 
        className="text-2xl font-semibold mb-6"
        style={{ 
          color: activeTheme.colors.text,
          fontFamily: activeTheme.fonts.body
        }}
      >
        <RichTextRenderer content={content.text} />
      </div>

      {/* Multiple Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center flex-wrap">
        {buttons.map((button, index) => {
          const isDisabled = button.mode === 'externalUrl' && !button.url
          
          return (
            <button
              key={button.id}
              onClick={() => handleButtonClick(button)}
              disabled={isDisabled}
              className={`
                inline-flex items-center px-6 py-3 text-base font-medium rounded-md transition-all
                focus:outline-none focus:ring-2 focus:ring-offset-2
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${index === 0 ? 'border-2' : 'border-2'}
              `}
              style={{
                backgroundColor: !isDisabled && index === 0
                  ? activeTheme.colors.primary
                  : 'transparent',
                color: !isDisabled && index === 0
                  ? activeTheme.colors.buttonText
                  : activeTheme.colors.primary,
                borderColor: activeTheme.colors.primary,
                '--tw-ring-color': activeTheme.colors.primary
              } as React.CSSProperties}
              title={isDisabled ? "URL not configured" : undefined}
            >
              {button.buttonText || "Button"}
            </button>
          )
        })}
      </div>
    </div>
  )
}
